/**
 * Animated GIF frames for the movie compositor.
 *
 * createImageBitmap keeps only the first frame, so a sticker looked frozen.
 * ImageDecoder returns each display frame; the playhead picks which one to draw.
 */

export interface GifStrip {
  width: number;
  height: number;
  frames: ImageBitmap[];
  /** Timeline ms at which each frame ends. */
  endsMs: number[];
  totalMs: number;
}

/** Which frame is showing at a clip-local time. The strip loops. */
export function gifFrameIndex(endsMs: readonly number[], totalMs: number, localMs: number): number {
  if (endsMs.length <= 1 || !(totalMs > 0)) return 0;
  let time = localMs % totalMs;
  if (time < 0) time += totalMs;
  for (let i = 0; i < endsMs.length; i += 1) {
    if (time < endsMs[i]) return i;
  }
  return endsMs.length - 1;
}

export function isGifMime(mime: string): boolean {
  return mime.toLowerCase().split(';')[0].trim() === 'image/gif';
}

interface DecodedFrame {
  displayWidth: number;
  displayHeight: number;
  duration: number | null;
  close(): void;
}

interface GifImageDecoder {
  tracks: {
    ready: Promise<void>;
    selectedTrack: { frameCount: number } | null;
  };
  decode(options: { frameIndex: number }): Promise<{ image: DecodedFrame }>;
  close(): void;
}

type GifImageDecoderCtor = new (init: { data: BufferSource; type: string }) => GifImageDecoder;

function decoderCtor(): GifImageDecoderCtor | null {
  const ctor = (globalThis as { ImageDecoder?: GifImageDecoderCtor }).ImageDecoder;
  return typeof ctor === 'function' ? ctor : null;
}

/** Browsers treat a 0 delay as 100 ms. Anything under two centiseconds is the same. */
function frameDelayMs(durationUs: number | null): number {
  const ms = durationUs == null ? 100 : durationUs / 1000;
  return ms < 20 ? 100 : ms;
}

/** Null when the file is a still, or this browser cannot decode GIF frames. */
export async function decodeGifStrip(blob: Blob): Promise<GifStrip | null> {
  const Ctor = decoderCtor();
  if (!Ctor) return null;
  const bytes = await blob.arrayBuffer();
  let decoder: GifImageDecoder | null = null;
  const frames: ImageBitmap[] = [];
  try {
    decoder = new Ctor({ data: bytes, type: 'image/gif' });
    await decoder.tracks.ready;
    const count = decoder.tracks.selectedTrack?.frameCount ?? 0;
    if (count <= 1) return null;
    const limit = Math.min(count, 300);
    const endsMs: number[] = [];
    let total = 0;
    let width = 0;
    let height = 0;
    for (let i = 0; i < limit; i += 1) {
      const { image } = await decoder.decode({ frameIndex: i });
      try {
        width = image.displayWidth || width;
        height = image.displayHeight || height;
        frames.push(await createImageBitmap(image));
        total += frameDelayMs(image.duration);
        endsMs.push(total);
      } finally {
        image.close();
      }
    }
    if (frames.length <= 1 || !(total > 0)) return null;
    const strip: GifStrip = { width, height, frames: frames.slice(), endsMs, totalMs: total };
    frames.length = 0;
    return strip;
  } catch {
    return null;
  } finally {
    frames.forEach((frame) => frame.close());
    decoder?.close();
  }
}
